import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import * as _ from 'lodash';
import {
    AisRestService,
    BaseQuestionIdentifier,
    CaseResource,
    ConditionDisclosureResource,
    ConditionsResourceIdentifier,
    DisclosurePhysicianResource,
    DisclosureSource,
    EmbeddableResourceNames,
    InterviewDisclosureIdentifier,
    ListDefinitionResource,
    PhysicianResource,
    QuestionAnswerType,
    ReflexiveAnswerType,
    ScreensQuestionResource
} from '@Components/mras/core';
import { PhysicianCaptureComponent } from '../physician-capture/physician-capture.component';
import { ScreensInterviewService } from '../../screens-interview.service';
import { ObservableService } from '@Services';
import { SharedModule } from '@Shared/shared.module';
import { BooleanInputComponent } from '../../controls/boolean-input.component';
import { PicklistInputComponent } from '../../controls/picklist-input.component';
import { DisclosureSearchInputComponent } from '../../controls/disclosure-search-input.component';
import { QuestionsReflexiveComponent } from '../questions-reflexive/questions-reflexive.component';
import { QuestionUnknownComponent } from '../question-unknown/question-unknown.component';
import { QuestionNonInteractiveComponent } from '../question-non-interactive/question-non-interactive.component';

@Component({
    selector: 'jhi-question-standard',
    templateUrl: './question-standard.component.html',
    styles: [],
    imports: [SharedModule, BooleanInputComponent, PhysicianCaptureComponent, 
        PicklistInputComponent, DisclosureSearchInputComponent, QuestionsReflexiveComponent, QuestionUnknownComponent,
    QuestionNonInteractiveComponent]
})
export class QuestionStandardComponent implements OnInit, OnChanges {

    readonly QuestionAnswerType = QuestionAnswerType;
    readonly DisclosureSource = DisclosureSource;
    readonly ReflexiveQuestionType = ReflexiveAnswerType;
    readonly Number: number;

    @Input()
        questionIdentifier: BaseQuestionIdentifier;
    searchIdentifier: ConditionsResourceIdentifier;

    @Input()
        summaryMode: boolean;

    @Input()
        caseDetails: CaseResource;

    @Input()
        question: ScreensQuestionResource;
    @Output()
        questionChange = new EventEmitter<ScreensQuestionResource>();

    @Input()
        disclosures: ConditionDisclosureResource[];
    @Output()
        disclosuresChange = new EventEmitter<ConditionDisclosureResource[]>();

    @Input()
        physicians: PhysicianResource[];
    @Output()
        physiciansChange = new EventEmitter<PhysicianResource[]>();

    @Input()
        disclosurePhysicians: DisclosurePhysicianResource[];
    @Output()
        disclosurePhysiciansChange = new EventEmitter<DisclosurePhysicianResource[]>();

    @Input()
        countries: ListDefinitionResource;

    specifiedDisclosure: ConditionDisclosureResource;
    answerValue: string;
    picklistAnswer: string;
    httpErrorMsg: string;
    removableDisclosure: boolean;
    filteredDisclosures: ConditionDisclosureResource[];

    constructor(private aisRestService: AisRestService,
        private screensInterviewService: ScreensInterviewService,
        public observableService: ObservableService
    ) {}

    ngOnInit(): void {
        if (this.disclosures == null) {
            throw Error("question-standard: disclosures are missing - should be present in 'embedded'");
        }
        this.searchIdentifier = new ConditionsResourceIdentifier();
        this.searchIdentifier.caseId = this.questionIdentifier.caseId;
        this.searchIdentifier.interviewCode = this.questionIdentifier.interviewCode;
        this.searchIdentifier.interviewInstance = this.questionIdentifier.interviewInstance;
        this.searchIdentifier.lifeInstance = this.questionIdentifier.lifeInstance;
        this.searchIdentifier.categoryCode = this.question.category.code;
        this.searchIdentifier.limit = 100;
        this.removableDisclosure = false;
        this.initialize();
        this.updateFilteredDisclosures();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.updateFilteredDisclosures();
    }

    answerBaseQuestion() {
        const resource = new ScreensQuestionResource();
        resource.answerValue = this.answerValue;
        this.httpErrorMsg = null;

        const linkedAlias = this.question.linkedAlias;

        this.aisRestService.answerBaseQuestion(this.questionIdentifier, resource).subscribe(
            {
                next: (e) => {
                    if (e.errorCode && e.errorCode !== '') {
                        this.clearFailedBaseQuestionAnswer();
                        this.httpErrorMsg = e.errorCode;
                        return;
                    }
                
                    Object.keys(this.question).forEach(f => delete this.question[f]);
                    Object.assign(this.question, e);
                    this.questionChange.emit(this.question);
                    const embeddedDisclosures = ((this.question._embedded || [])[EmbeddableResourceNames.DISCLOSURES] ||
                    []) as ConditionDisclosureResource[];

                    if (!embeddedDisclosures || embeddedDisclosures.length <= 0) {
                        // look for any disclosures we already have and remove them

                        const oldDisclosures =  this.disclosures.filter(f => f.aliasName === linkedAlias && f.baseQuestionsDisclosures?.find(bq => bq.baseQuestionCode === this.question.code));
                        oldDisclosures.forEach(oldDisclosure => Object.keys(oldDisclosure).forEach(f => delete oldDisclosure[f]));
                    }

                    embeddedDisclosures.forEach(newDisclosure => {
                        const oldDisclosure = this.disclosures.find(f => f.aliasName === newDisclosure.aliasName && f.baseQuestionsDisclosures?.find(bq => bq.baseQuestionCode === this.question.code));
                        if (oldDisclosure == null) {
                            this.disclosures.push(newDisclosure);
                            return;
                        }
                        Object.keys(oldDisclosure).forEach(f => delete oldDisclosure[f]);
                        Object.assign(oldDisclosure, newDisclosure);
                    });
                    this.disclosuresChange.emit(this.disclosures);
                    this.initialize();
                    this.updateFilteredDisclosures();
                    
                    this.observableService.onMrasQuestionUpdated.next(1);
                },
                error: errMsg => {
                    this.clearFailedBaseQuestionAnswer();
                    this.httpErrorMsg = errMsg;
                }
            });
    }

    private clearFailedBaseQuestionAnswer() {
        if (!this.aisRestService.isClearFailedMrasAnswersEnabled()) {
            return;
        }

        this.answerValue = null;
        this.picklistAnswer = null;
        this.question.answerValue = null;
    }

    private initialize() {
        this.specifiedDisclosure = null;
        this.answerValue = this.question.answerValue;
        if (this.question.disclosureSource === DisclosureSource.SPECIFIED) {
            this.specifiedDisclosure = this.disclosures.find(e => e.aliasName === this.question.linkedAlias && e.baseQuestionsDisclosures?.find(bq => bq.baseQuestionCode === this.question.code));
        } else if (this.question.disclosureSource === DisclosureSource.PICKLIST) {
            if (this.question.disclosures == null) {
                this.picklistAnswer = '';
            } else {
                this.picklistAnswer = this.question.disclosures.map(e => e.aliasName).join('COMMA');
            }
        }
    }

    handleDisclosurePickListEvent(change: any) {
        const disclosureName = change.name;
        
        const isMultipleChoice = !this.question.properties || 
        this.question.properties['MULTIPLE.CHOICE'] !== 'False';

        if (disclosureName === 'noneOfTheAbove') {
            this.handleNoneOfTheAbove(change);
        } else if (change.value) {
            this.addDisclosure(disclosureName, isMultipleChoice);

            if (isMultipleChoice) {
                const values = this.picklistAnswer.split('COMMA') || [];
                values.push(disclosureName);
                this.picklistAnswer = values.join('COMMA');
            }else{
                this.picklistAnswer = disclosureName;
            }
        } else {
            this.removeDisclosure(disclosureName);
            if (isMultipleChoice) {
            this.picklistAnswer = this.picklistAnswer
                .split('COMMA')
                .filter(e => e !== disclosureName)
                .join('COMMA');
            }
        }
    }

    handleNoneOfTheAbove(change: any) {
        this.answerValue = change.value;
        this.answerBaseQuestion();
    }

    handleDisclosureSearchEvent(disclosureName: string) {
        this.addDisclosure(disclosureName);
    }

    interviewDisclosureIdentifier(disclosureName: string) {
        const identifier = new InterviewDisclosureIdentifier();
        identifier.aliasName = disclosureName;
        identifier.baseQuestionCode = this.questionIdentifier.baseQuestionCode;
        identifier.interviewCode = this.questionIdentifier.interviewCode;
        identifier.lifeInstance = this.questionIdentifier.lifeInstance;
        identifier.caseId = this.questionIdentifier.caseId;
        return identifier;
    }

    updateFilteredDisclosures() {
        const chain = [] as ConditionDisclosureResource[];
        const createChain = (disclosure: ConditionDisclosureResource, refresh: boolean) => {
            if (disclosure == null || chain.find(e => e.aliasName === disclosure.aliasName) != null) {
                return;
            }
            chain.push(disclosure);
            if (disclosure.triggeredCondAliases != null && disclosure.triggeredCondAliases.length > 0) {
                for (let i = 0; i < disclosure.triggeredCondAliases.length; i++) {
                    const triggeredDisclosure = _.find(this.disclosures, f => f.aliasName === disclosure.triggeredCondAliases[i] && f.baseQuestionsDisclosures?.find(bq => bq.baseQuestionCode === this.question.code));
                    createChain(triggeredDisclosure, refresh);
                }
            }
        };
        for (const identifier of this.question.disclosures || []) {
            const disclosure = this.disclosures.find(f => f.aliasName === identifier.aliasName && f.baseQuestionsDisclosures?.find(bq => bq.baseQuestionCode === this.question.code));            

            createChain(disclosure, false);
        }
        this.filteredDisclosures = chain;
    }

    handleDisclosureRemove(disclosureName: string) {
        this.removeDisclosure(disclosureName);
        if (this.question.disclosureSource === DisclosureSource.PICKLIST) {
            this.picklistAnswer = this.picklistAnswer
                .split('COMMA')
                .filter(e => e !== disclosureName)
                .join('COMMA');
        }
        this.updateFilteredDisclosures();
    }

    private removeDisclosure(disclosureName: string) {
        const disclosureIdentifier = new InterviewDisclosureIdentifier();
        disclosureIdentifier.baseQuestionCode = this.questionIdentifier.baseQuestionCode;
        disclosureIdentifier.caseId = this.questionIdentifier.caseId;
        disclosureIdentifier.interviewCode = this.questionIdentifier.interviewCode;
        disclosureIdentifier.interviewInstance = this.questionIdentifier.interviewInstance;
        disclosureIdentifier.lifeInstance = this.questionIdentifier.lifeInstance;
        disclosureIdentifier.languageCode = this.questionIdentifier.languageCode;
        disclosureIdentifier.aliasName = disclosureName;
        const triggeredCondAliases = [];
        const disclosureToRemove = this.disclosures.find(d => d.aliasName === disclosureName && d.baseQuestionsDisclosures?.find(bq => bq.baseQuestionCode === this.question.code));
        if (disclosureToRemove != null && disclosureToRemove.triggeredCondAliases != null) {
            this.disclosures
                .filter(d => d.aliasName === disclosureName && d.baseQuestionsDisclosures?.find(bq => bq.baseQuestionCode === this.question.code))
                .forEach(function(disclosure) {
                    triggeredCondAliases.push(disclosure.aliasName);
                });
        }
        this.aisRestService.deleteDisclosure(disclosureIdentifier).subscribe(
            success => {
                _.remove(
                    this.disclosures,
                    f => f.aliasName === disclosureName && f.baseQuestionsDisclosures?.find(bq => bq.baseQuestionCode === this.question.code) && f.removableFromBqCodes != null && f.removableFromBqCodes.length === 1
                );
                _.remove(this.question.disclosures, f => f.aliasName === disclosureName && f.baseQuestionCode === this.question.code);
                this.removeTriggeredDisclosures(triggeredCondAliases);
            },
            errMsg => (this.httpErrorMsg = errMsg)
        );
    }

    removeTriggeredDisclosures(triggeredCondAliases: string[]) {
        if (triggeredCondAliases != null) {
            for (let i = 0; i < triggeredCondAliases.length; i++) {
                const triggeredCondAlias = triggeredCondAliases[i];
                if (triggeredCondAlias === null) {
                    return;
                }
                let nextTriggeredCondAliases = null;
                const disclosure = this.disclosures.find(d => d.aliasName === triggeredCondAlias && d.baseQuestionsDisclosures?.find(bq => bq.baseQuestionCode === this.question.code));
                if (disclosure != null && disclosure.triggeredCondAliases != null) {
                    nextTriggeredCondAliases = this.disclosures.find(d => d.aliasName === triggeredCondAlias && d.baseQuestionsDisclosures?.find(bq => bq.baseQuestionCode === this.question.code)).triggeredCondAliases;
                }
                _.remove(this.disclosures, f => f.aliasName === triggeredCondAlias && f.removableFromBqCodes === null && f.baseQuestionsDisclosures?.find(bq => bq.baseQuestionCode === this.question.code));
                _.remove(this.question.disclosures, f => f.aliasName === triggeredCondAlias && f.disclosures?.find(bq => bq.baseQuestionCode === this.question.code));
                this.removeTriggeredDisclosures(nextTriggeredCondAliases);
            }
        }
        this.updateFilteredDisclosures();

        
        this.observableService.onMrasQuestionUpdated.next(1);
    }

    private addDisclosure(disclosureName: string, isMultipleChoice: boolean = false) {
        const resource = new ConditionDisclosureResource();
        resource.aliasName = disclosureName;
        this.aisRestService.createDisclosure(this.questionIdentifier, resource).subscribe(
            createDisclosureResp => {
                this.resetBaseQuestion();
            },
            errMsg => {
                if (this.aisRestService.isClearFailedMrasAnswersEnabled()) {
                    if (isMultipleChoice) {
                        this.picklistAnswer = (this.picklistAnswer || '')
                            .split('COMMA')
                            .filter(e => e !== disclosureName)
                            .join('COMMA');
                    } else if (this.picklistAnswer === disclosureName) {
                        this.picklistAnswer = '';
                    }
                }

                this.httpErrorMsg = errMsg;
            }
        );
    }

    resetBaseQuestion() {
        this.aisRestService.getBaseQuestion(this.questionIdentifier).subscribe(getBaseQuestionResp => {
            Object.keys(this.question).forEach(f => delete this.question[f]);
            Object.assign(this.question, getBaseQuestionResp);
            this.questionChange.emit(this.question);

            const disclosures = (getBaseQuestionResp._embedded[EmbeddableResourceNames.DISCLOSURES] as ConditionDisclosureResource[]).filter(e => e.baseQuestionsDisclosures?.find(bq => bq.baseQuestionCode === this.question.code));
            disclosures.forEach(e => {
                _.remove(this.disclosures, f => f.aliasName === e.aliasName && f.baseQuestionsDisclosures?.find(bq => bq.baseQuestionCode === this.question.code));
                this.disclosures.push(e);   
            });
            this.disclosuresChange.emit(this.disclosures);
            this.updateFilteredDisclosures();
            
            this.observableService.onMrasQuestionUpdated.next(1);
        });
    }

    handleDisclosureUpdate(disclosureName: string) {
        _.remove(this.disclosures, f => f.aliasName === disclosureName && f.baseQuestionsDisclosures?.find(bq => bq.baseQuestionCode === this.question.code));
        const triggeredIdentifier = this.interviewDisclosureIdentifier(disclosureName);
        this.aisRestService.getDisclosure(triggeredIdentifier).subscribe(e => {
            this.disclosures.push(e);
            this.updateFilteredDisclosures();

            this.questionChange.emit(this.question);
        });
    }
}
